import React from 'react'

import { useNavigate } from 'react-router-dom';
import { REBUILD_CLASSIFICATIONS, optimizeImageSrc } from '../utils/shared.js';
import useAnalytics from '../hooks/useAnalytics';

function formatPrice(price) {
    if (!price || price === 0) return 'On Request';
    return new Intl.NumberFormat('en-ZA', {
        style: 'currency',
        currency: 'ZAR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(price);
}

export default function ProductCard({ product, onViewDetails, badgeType, user, basket, onAddToBasket, onUpdateBasketQuantity, onRemoveFromBasket, onShowLogin, savedItems, onToggleSave }) {
    const navigate = useNavigate();
    const { trackEvent, consent } = useAnalytics();
    const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
    const [showDieselQuoteModal, setShowDieselQuoteModal] = React.useState(false);
    const [priceRequested, setPriceRequested] = React.useState(false); // FIX: Define priceRequested state
    const [priceRequestSuccess, setPriceRequestSuccess] = React.useState(false); // FIX: Define priceRequestSuccess state

    try {
        const cleanPath = (path) => {
            if (!path) return null;
            // Allow web URLs and data URIs
            if (path.match(/^(http|https|data):/i)) return path;

            // 1. Decode URI component to handle %20 correctly
            let clean = decodeURIComponent(path).replace(/\\/g, '/').trim();

            // 2. Remove full project path if present at the beginning
            if (clean.startsWith('/miners-market-main/')) {
                clean = clean.substring('/miners-market-main/'.length);
            }

            // 3. Ensure path is absolute from web root (required since app is served from /app/ base)
            if (!clean.startsWith('/')) {
                clean = '/' + clean;
            }

            // Strategy: Locate 'uploads/' or 'images/' to anchor the path relative to project root
            const anchorMatch = clean.match(/(uploads\/|images\/|miscellaneous items\/)/i);
            if (anchorMatch) {
                return clean.substring(anchorMatch.index - 1);
            }
            if (clean.includes(':')) return null;
            return clean;
        };

        const productImages = React.useMemo(() => {
            const allImages = [];
            if (product.images && Array.isArray(product.images)) {
                allImages.push(...product.images);
            }
            // --- NEW: Handle Media Array ---
            if (product.media && Array.isArray(product.media)) {
                const mediaImages = product.media.filter(m => m.media_type === 'image').map(m => m.file_path);
                allImages.push(...mediaImages);
            }
            for (let i = 1; i <= 25; i++) {
                if (product[`image_url_${i}`]) {
                    allImages.push(product[`image_url_${i}`]);
                }
            }
            let uniqueImages = [...new Set(allImages.map(p => cleanPath(p)).filter(Boolean))];
            
            // --- NEW: Add default bearing images to the card grid if no user images exist ---
            if (product.bearing_type && uniqueImages.length === 0) {
                if (product.bearing_type === 'Double Direction Tapered Roller Thrust Bearing') {
                    uniqueImages = ['images/products/Tapered Roller Thrust Bearings.png'];
                } else if (product.bearing_type.includes('Self-Aligning Ball Bearing')) {
                    uniqueImages = [
                        'images/products/Self-Aligning Ball Bearing.png',
                        'images/products/Self-Aligning Ball Bearings Box.png'
                    ];
                } else if (product.bearing_type === 'Inner Ring Four Row Cylindrical Roller Bearing') {
                    uniqueImages = ['images/products/Inner Ring Four Row Cylindrical Roller Bearing Packaging.png'];
                } else if (product.bearing_type.includes('Spherical Roller Bearing')) {
                    uniqueImages = [
                        'Miscellaneous Items/KG ULTRA BEARING IMAGES/Spherical roller bearing.png',
                        'Miscellaneous Items/KG ULTRA BEARING IMAGES/Spherical roller bearing box.png'
                    ];
                }
            }

            if (uniqueImages.length === 0) {
                uniqueImages = ['images/logo/MM Logo.jpg'];
            }
            return uniqueImages;
        }, [product]);

        // --- NEW: Check if product has documents ---
        const hasDocuments = React.useMemo(() => {
            if (product.media && Array.isArray(product.media)) {
                return product.media.some(m => m.media_type === 'document');
            }
            // Check legacy doc fields
            const docFields = ['doc_manual_url', 'doc_datasheet_url', 'doc_conformity_url', 'doc_calibration_url', 'doc_warranty_url', 'doc_service_history_url', 'doc_factory_test_url', 'doc_oil_report_url'];
            return docFields.some(field => product[field]);
        }, [product]);

        const [imageError, setImageError] = React.useState(false); // State to track image loading errors

        // --- NEW: Reset error state when index or product changes ---
        React.useEffect(() => {
            setImageError(false);
            setCurrentImageIndex(0); // Ensure the card resets to the first photo when data changes
            // Check if price has been requested for this product
            const requested = localStorage.getItem(`price_requested_${product.listingType}_${product.id}`);
            setPriceRequested(!!requested);
        }, [product.id, product.listingType]);


        const handleImageNav = (e, direction) => {
            if (e && e.stopPropagation) e.stopPropagation();
            setCurrentImageIndex(prev => (prev + direction + productImages.length) % productImages.length);
            if (consent === 'granted') {
                trackEvent('product_gallery_interacted', {
                    productId: product.id,
                    listingType: product.listingType,
                    listingNumber: product.listing_number || '',
                    productName: product.name || '',
                    eventData: { direction, imageIndex: (prev + direction + productImages.length) % productImages.length },
                });
            }
        };

        const [touchStart, setTouchStart] = React.useState(0);
        const handleTouchStart = (e) => {
            setTouchStart(e.targetTouches[0].clientX);
        };
        const handleTouchEnd = (e) => {
            const touchEnd = e.changedTouches[0].clientX;
            const swipeDistance = touchStart - touchEnd;
            if (Math.abs(swipeDistance) > 50) { // Threshold for swipe
                if (swipeDistance > 0) {
                    handleImageNav(null, 1);
                } else {
                    handleImageNav(null, -1);
                }
            }
        };

        const badgeSrc = {
            'wots': 'images/badges/MM Badge.png',
            'standard': 'images/badges/MM Badge.png'
        };

        // The price from the database is already the final, inflated price.
        const totalAmount = parseFloat(product.price) || 0;

        // --- NEW: Calculate price range for consumables with per-size pricing ---
        const consumableMinPrice = product.listingType === 'consumable' && product.min_size_price ? parseFloat(product.min_size_price) : null;
        const consumableMaxPrice = product.listingType === 'consumable' && product.max_size_price ? parseFloat(product.max_size_price) : null;
        const hasPerSizePricing = consumableMinPrice !== null && consumableMaxPrice !== null && consumableMinPrice !== consumableMaxPrice;

        const formatPriceRange = (min, max) => {
            if (min === max || !max) return formatPrice(min);
            return `${formatPrice(min)} - ${formatPrice(max)}`;
        };

        
                
        // Only standard and WOTS listings can have a price on request.
        const canBeOnRequest = ['standard', 'wots', 'master_bearing', 'conveyor', 'filter', 'grease', 'kg_pps', 'service'].includes(product.listingType);
        // Diesel products need special quote handling
        const isDiesel = product.listingType === 'diesel' || product.name?.toLowerCase().includes('diesel');

        // --- NEW: Basket Logic (Consumables, Bearings & Filters with Price) ---
        const isBasketProduct = product.listingType === 'consumable' || 
                               (product.bearing_type && totalAmount > 0) ||
                               (product.listingType === 'filter' && totalAmount > 0);
        const itemInBasket = isBasketProduct && basket ? basket.find(item => item.id === product.id && item.listingType === product.listingType) : null;
        const quantityInBasket = itemInBasket ? itemInBasket.quantity : 0;
        // --- NEW: Saved Item Logic ---
        const isSaved = savedItems && savedItems.some(item => item.id === product.id && item.listingType === product.listingType);


        const handleAddToCart = (e) => {
            e.stopPropagation(); // Prevent modal from opening
            // For consumables, always open modal to force size/quantity selection
            if (product.listingType === 'consumable') {
                onViewDetails();
            } else {
                onAddToBasket(product, 1, null, true); // Explicitly show global success message
            }
        };

        const handleIncreaseQuantity = (e) => {
            e.stopPropagation();
            onUpdateBasketQuantity(product.id, product.listingType, quantityInBasket + 1);
        };

        const handleImageError = (e) => {
            // Called when the image fails to load
            if (imageError) return; // Prevent loop if fallback also fails
            const failedUrl = e?.target?.src || 'Unknown URL';
            console.warn(`Failed to load image for product ${product.id}. URL: ${failedUrl}`);
            setImageError(true);
        };

        const handleDecreaseQuantity = (e) => {
            e.stopPropagation();
            if (quantityInBasket - 1 > 0) {
                onUpdateBasketQuantity(product.id, product.listingType, quantityInBasket - 1);
            } else {
                // If quantity becomes 0, remove it from the basket.
                onRemoveFromBasket(product.id, product.listingType);
            }
        };

        const handleClick = () => {
            // If this is a master bearing product, navigate to the new variant page.
            if (product.listingType === 'master_bearing') {
                navigate(`/bearing/${product.slug}`);
            } else {
                // Otherwise, use the default behavior (open modal).
                onViewDetails();
            }
            if (consent === 'granted') {
                trackEvent('product_card_clicked', {
                    productId: product.id,
                    listingType: product.listingType,
                    listingNumber: product.listing_number || '',
                    productName: product.name || '',
                    categoryId: product.category_id ?? null,
                    categoryName: product.category_name || '',
                });
            }
        };

        const handleDieselQuote = (e) => {
            e.stopPropagation();
            if (!user) {
                onShowLogin();
                return;
            }
            setShowDieselQuoteModal(true);
        };

        const sendDieselQuote = async (formData) => {
            try {
                const response = await fetch('/api/send_diesel_quote.php', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        ...formData,
                        product_name: product.name,
                        listing_number: product.listing_number
                    })
                });

                const result = await response.json();
                if (!response.ok) throw new Error(result.error || 'Failed to send diesel quote');
                return result;
            } catch (err) {
                throw new Error(err.message);
            }
        };

        const handleShare = async (e) => {
            e.stopPropagation();
            const shareUrl = `${window.location.origin}/${product.listingType === 'master_bearing' ? `bearing/${product.slug}` : `?viewProduct=${product.id}&viewType=${product.listingType}`}`;
            
            const shareText = `Check out this listing on Miners Market:\n\n${product.name}\nListing #: ${product.listing_number}\n\n${shareUrl}`;

            // Try Web Share API first (mobile devices)
            if (navigator.share) {
                try {
                    await navigator.share({
                        title: product.name,
                        text: shareText,
                        url: shareUrl
                    });
                    return;
                } catch (err) {
                    if (err.name !== 'AbortError') {
                        console.warn('Web Share API failed, falling back to clipboard:', err);
                    }
                }
            }

            // Fallback: Copy to clipboard with user feedback
            try {
                await navigator.clipboard.writeText(shareText);
                
                // Create a temporary toast notification
                const toast = document.createElement('div');
                toast.className = 'fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in';
                toast.textContent = 'Link copied to clipboard!';
                document.body.appendChild(toast);
                
                setTimeout(() => {
                    toast.style.opacity = '0';
                    toast.style.transition = 'opacity 0.3s';
                    setTimeout(() => document.body.removeChild(toast), 300);
                }, 2000);
            } catch (err) {
                console.error('Clipboard copy failed:', err);
                alert('Failed to copy link. Please copy manually:\n\n' + shareText);
            }
        };

        return (
            <React.Fragment>
            <div onClick={handleClick} className="bg-white rounded-xl shadow-lg overflow-hidden card-hover flex flex-col cursor-pointer">
                {/* Image container with relative positioning for the badge */}
                <div 
                    className="relative"
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                >
                    <img
                        className="w-full aspect-square object-contain bg-white lazyload"
                        loading="lazy"
                        decoding="async"
                        onError={handleImageError} // Use the function from here
                        src={optimizeImageSrc(
                            imageError
                                 ? 'images/logo/MM Logo.jpg' //fallback image
                                 : productImages[currentImageIndex],
                            400
                        )}
                        alt={product.name} 
                    />
                    {productImages.length > 1 && (
                        <>
                            <button onClick={(e) => handleImageNav(e, -1)} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/30 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-black/50 transition-colors z-10">
                                <i className="fas fa-chevron-left text-sm"></i>
                            </button>
                            <button onClick={(e) => handleImageNav(e, 1)} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/30 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-black/50 transition-colors z-10">
                                <i className="fas fa-chevron-right text-sm"></i>
                            </button>
                            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-1.5 z-10">
                                {productImages.slice(0, 20).map((_, index) => (
                                    <div key={index} className={`w-2 h-2 rounded-full transition-colors ${index === currentImageIndex ? 'bg-white' : 'bg-white/50'}`}></div>
                                ))}
                            </div>
                        </>
                    )}
                                        {/* KG Logo for Bearings */}
                    {product.bearing_type && product.brand && product.brand.toUpperCase().includes('KG') && (
                         <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                            <img src={optimizeImageSrc('images/logo/kg-bearings-logo.webp', 120)} alt="KG Bearings" className="w-6 md:w-12 h-auto object-contain" />
                         </div>
                    )}
                    {/* FAG Logo for Bearings */}
                    {product.bearing_type && product.brand && product.brand.toUpperCase().includes('FAG') && (
                         <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                            <img src={optimizeImageSrc('Miscellaneous Items/Logos-20260206T070056Z-3-001/Logos/FAG.png', 120)} alt="FAG Bearings" className="w-6 md:w-12 h-auto object-contain" />
                         </div>
                    )}
                    {/* NTN Logo for Bearings */}
                    {product.bearing_type && product.brand && product.brand.toUpperCase().includes('NTN') && (
                         <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                            <img src={optimizeImageSrc('Miscellaneous Items/Logos-20260206T070056Z-3-001/Logos/NTN.png', 120)} alt="NTN Bearings" className="w-6 md:w-12 h-auto object-contain" />
                         </div>
                    )}
                    {/* KG Logo for Filters */}
                    {product.listingType === 'filter' && (
                         <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                            <img src={optimizeImageSrc('Miscellaneous Items/Logos-20260206T070056Z-3-001/Logos/KG-Filter-high-res-logo blue.png', 200)} alt="KG Filters" className="w-12 md:w-24 h-auto object-contain" />
                         </div>
                    )}
                    {/* ARB Logo for Conveyor Belts */}
                    {product.listingType === 'conveyor' && (
                         <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                            <img src={optimizeImageSrc('Miscellaneous Items/Logos-20260206T070056Z-3-001/Logos/arb-logo.png', 160)} alt="ARB" className="w-10 md:w-20 h-auto object-contain" />
                         </div>
                    )}
                    {/* KG Logo for Greases */}
                    {product.listingType === 'grease' && (
                         <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                            <img src="images/logo/kg-bearings-logo.webp" alt="KG Ultra" className="w-6 md:w-12 h-auto object-contain" />
                         </div>
                    )}
                    {/* KG Logo for PPS */}
                    {product.listingType === 'kg_pps' && (
                         <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                            <img src="images/logo/kg-bearings-logo.webp" alt="KG ULTRA" className="w-6 md:w-12 h-auto object-contain" />
                         </div>
                    )}

                    {/* Badge is now rendered based on the badgeType prop passed from the parent page */}
                    {badgeType && badgeSrc[badgeType] && (
                        <img
                            src={badgeSrc[badgeType]}
                            alt={`${badgeType} listing badge`}
                            className="absolute top-2 right-2 w-8 h-8 md:w-16 md:h-16 object-contain"
                        />
                    )}
                    {/* --- NEW: Document Badge --- */}
                    {hasDocuments && (
                        <div className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full shadow-lg z-20" title="Documents Available">
                            <i className="fas fa-file-alt text-xs md:text-sm"></i>
                        </div>
                    )}
                    {/* --- NEW: Share Icon --- */}
                    <button
                        onClick={handleShare}
                        className="absolute bottom-2 left-2 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/30 backdrop-blur-md text-gray-900 border border-white/20 flex items-center justify-center transition-all hover:bg-white/50 z-20 shadow-sm"
                        title="Share Listing"
                    >
                        <i className="fas fa-share-alt text-sm md:text-base"></i>
                    </button>
                    {/* --- NEW: Save Icon --- */}
                    {onToggleSave && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onToggleSave(product); }}
                            className={`absolute bottom-2 right-2 w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center transition-all duration-200 transform hover:scale-110 ${isSaved ? 'bg-red-500/80 text-white' : 'bg-black/40 text-white backdrop-blur-sm'}`}
                            title={isSaved ? "Unsave Item" : "Save for Later"}
                        >
                            <i className={`${isSaved ? 'fas' : 'far'} fa-heart text-sm md:text-base`}></i>
                        </button>
                    )}
                </div>
                <div className="p-4 md:p-6 flex-grow flex flex-col">
                    <h3 className="text-lg font-bold text-gray-900 truncate" title={product.name}>{product.name}</h3>
                    <p className="text-sm text-gray-500 mb-2 truncate">{product.category_name}</p>
                    
                    {/* --- NEW: Technical Specs Summary --- */}
                    {(product.bearing_type || (product.listingType === 'filter' && product.filter_type) || product.application || product.specifications) && (
                        <div className="mb-3 grid grid-cols-2 gap-x-2 gap-y-1 text-[10px] text-gray-500 border-t border-gray-100 pt-2">
                            {product.bearing_type && (
                                <>
                                    {product.d_inner && <div className="truncate">ID: <span className="font-semibold text-gray-700">{product.d_inner}mm</span></div>}
                                    {product.d_outer && <div className="truncate">OD: <span className="font-semibold text-gray-700">{product.d_outer}mm</span></div>}
                                    {product.width && <div className="truncate">W: <span className="font-semibold text-gray-700">{product.width}mm</span></div>}
                                    {product.clearance && <div className="truncate">Clr: <span className="font-semibold text-gray-700">{product.clearance}</span></div>}
                                </>
                            )}
                            {product.listingType === 'filter' && product.filter_type && (
                                <div className="col-span-2 truncate font-medium text-blue-600 uppercase tracking-tighter">{product.filter_type}</div>
                            )}
                            {!product.bearing_type && product.listingType !== 'filter' && (
                                <>
                                    {product.application && <div className="col-span-2 truncate">App: <span className="font-semibold text-gray-700">{product.application}</span></div>}
                                    {product.specifications && <div className="col-span-2 line-clamp-2">Specs: <span className="font-semibold text-gray-700">{product.specifications}</span></div>}
                                    {product.quantity > 1 && <div className="truncate">Qty: <span className="font-semibold text-gray-700">{product.quantity}</span></div>}
                                    {product.location && <div className="truncate">Loc: <span className="font-semibold text-gray-700">{product.location}</span></div>}
                                </>
                            )}
                        </div>
                    )}

                    {/* --- NEW: Brand Tags (Moved outside conditional so they show for basket items too) --- */}
                    {product.bearing_type && (
                        <div className="mb-2 mt-auto">
                            <span className={`${product.brand && product.brand.includes('KG') ? 'bg-blue-900' : 'bg-gray-800'} text-white text-xs font-bold px-2 py-1 rounded uppercase tracking-wider`}>
                                Brand: {product.brand || 'KG ULTRA'}
                            </span>
                        </div>
                    )}
                    {product.listingType === 'filter' && (
                        <div className="mb-2 mt-auto">
                            <span className="bg-blue-900 text-white text-xs font-bold px-2 py-1 rounded uppercase tracking-wider">
                                Brand: {product.brand || 'KG Filters'}
                            </span>
                        </div>
                    )}
                    {product.listingType === 'conveyor' && (
                        <div className="mb-2 mt-auto">
                            <span className="bg-blue-900 text-white text-xs font-bold px-2 py-1 rounded uppercase tracking-wider">
                                Brand: {product.brand || 'ARB PLUS'}
                            </span>
                        </div>
                    )}
                    {product.listingType === 'grease' && (
                        <div className="mb-2 mt-auto">
                            <span className="bg-blue-900 text-white text-xs font-bold px-2 py-1 rounded uppercase tracking-wider">
                                Brand: {product.brand || 'KG Ultra'}
                            </span>
                        </div>
                    )}
                    {product.listingType === 'kg_pps' && (
                        <div className="mb-2 mt-auto">
                            <span className="bg-blue-900 text-white text-xs font-bold px-2 py-1 rounded uppercase tracking-wider">
                                Brand: {product.brand || 'KG ULTRA'}
                            </span>
                        </div>
                    )}

                    {/* --- NEW: Rebuild Classification Badge --- */}
                    {product.listingType === 'rebuild' && product.rebuild_classification && (() => {
                        const classification = REBUILD_CLASSIFICATIONS.find(c => c.id === product.rebuild_classification);
                        if (!classification) return null;
                        return (
                            <div className={`mt-2 inline-flex items-center px-2.5 py-0.5 rounded-md border text-xs font-bold ${classification.badgeClasses} w-fit`}>
                                <i className={`${classification.icon} mr-1.5`}></i>
                                {classification.title.split(':')[0]} {/* e.g. "Category A" */}
                            </div>
                        );
                    })()}

                    {/* --- NEW: Conditional UI for basket products vs other products --- */}
                    {isBasketProduct && (!user || user?.activeRole === 'buyer') ? (
                        <div className={`${product.bearing_type ? 'pt-2' : 'mt-auto pt-4'}`}>
                            <div className="flex justify-between items-center">
                                <p className="text-lg md:text-2xl font-bold text-blue-600">
                                    {product.listingType === 'consumable' && hasPerSizePricing
                                        ? formatPriceRange(consumableMinPrice, consumableMaxPrice)
                                        : formatPrice(totalAmount)}
                                </p>
                                {quantityInBasket === 0 ? (
                                    <button onClick={handleAddToCart} className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center text-white transition-colors shadow-sm">
                                        <i className="fas fa-plus text-sm md:text-base"></i>
                                    </button>
                                ) : (
                                    <div className="flex items-center gap-1 md:gap-2">
                                        <button onClick={handleDecreaseQuantity} className="w-6 h-6 md:w-7 md:h-7 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-gray-700 transition-colors"><i className="fas fa-minus text-[10px] md:text-xs"></i></button>
                                        <span className="font-semibold text-base md:text-lg">{quantityInBasket}</span>
                                        <button onClick={handleIncreaseQuantity} className="w-6 h-6 md:w-7 md:h-7 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-gray-700 transition-colors"><i className="fas fa-plus text-[10px] md:text-xs"></i></button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className={`${(product.bearing_type || product.listingType === 'filter' || product.listingType === 'conveyor' || product.listingType === 'grease' || product.listingType === 'kg_pps' || isDiesel) ? 'pt-2' : 'mt-auto pt-4'}`}>
                            <div className="flex justify-between items-end">
                                {isDiesel ? (
                                    <div className="w-full">
                                        <div className="primary-gradient text-white text-center font-bold px-4 py-2 rounded-lg text-sm cursor-pointer" onClick={handleDieselQuote}>
                                            Request Diesel Quote
                                        </div>
                                    </div>
                                ) : canBeOnRequest && (product.is_price_on_request || !product.price || parseFloat(product.price) <= 0) ? (
                                    <div className="w-full">
                                        {priceRequestSuccess ? (
                                            <div className="bg-green-100 border border-green-300 text-green-800 text-center font-bold px-4 py-2 rounded-lg text-sm animate-fade-in">
                                                <i className="fas fa-check-circle mr-2"></i>Price Requested Successfully
                                            </div>
                                        ) : priceRequested ? (
                                            <div className="text-center">
                                                <div className="bg-gray-300 text-gray-600 text-center font-bold px-4 py-2 rounded-lg text-sm cursor-not-allowed">
                                                    Price Requested
                                                </div>
                                                <p className="text-xs text-gray-500 mt-1">As soon as price has been accepted please check your Buyer Dashboard</p>
                                            </div>
                                        ) : (
                                            <div 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (!user) {
                                                        onShowLogin();
                                                        return;
                                                    }
                                                    // Request price logic
                                                    fetch('/api/request_price.php', {
                                                        method: 'POST',
                                                        credentials: 'include',
                                                        headers: { 'Content-Type': 'application/json' },
                                                        body: JSON.stringify({
                                                            product_id: product.id,
                                                            listing_type: product.listingType || product.listing_type || 'standard'
                                                        })
                                                    })
                                                    .then(async response => {
                                                        const text = await response.text();
                                                        let result = {};
                                                        try {
                                                            result = text ? JSON.parse(text) : {};
                                                        } catch (e) {
                                                            result = { error: 'Invalid response from server' };
                                                        }
                                                        if (!response.ok) {
                                                            throw new Error(result.error || `Request failed with status ${response.status}`);
                                                        }
                                                        return result;
                                                    })
                                                    .then(result => {
                                                        setPriceRequestSuccess(true);
                                                        // Mark as requested in localStorage
                                                        localStorage.setItem(`price_requested_${product.listingType}_${product.id}`, 'true');
                                                        setPriceRequested(true);
                                                        // Hide success message after 3 seconds
                                                        setTimeout(() => setPriceRequestSuccess(false), 3000);
                                                    })
                                                    .catch(err => {
                                                        alert('Error requesting price: ' + err.message);
                                                    });
                                                }}
                                                className="primary-gradient text-white text-center font-bold px-4 py-2 rounded-lg text-sm cursor-pointer"
                                            >
                                                Request Price
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <div className="min-w-0">
                                            <p className="text-lg md:text-2xl font-bold text-blue-600 whitespace-nowrap truncate">{formatPrice(totalAmount)}</p>
                                            <p className="text-xs text-gray-500">{product.listingType === 'rebuild' ? 'Base Price (Before Work Done)' : 'Total Price (incl. fee)'}</p>
                                        </div>
                                        <span className="text-blue-600 hover:text-blue-800 font-semibold text-sm">View</span>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {showDieselQuoteModal && <DieselQuoteModal
                isOpen={showDieselQuoteModal}
                onClose={() => setShowDieselQuoteModal(false)}
                product={product}
                user={user}
                onSendQuote={sendDieselQuote}
            />}
            </React.Fragment>
        );
    } catch (error) {
        console.error('ProductCard component error:', error);
        return <div>Error loading product</div>;
    }
}
